import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useIsMobile } from '../hooks/useMediaQuery';
import { useSmartBack } from '../hooks/useSmartBack';
import { useScrollLock } from '../hooks/useScrollLock';
import Alert from '../components/Alert';
import { useAuthStore } from '../stores/authStore';
import {
  getPartnerDetail,
  getPartnerContacts,
  updatePartner,
  deletePartner,
  createPartnerContact,
  updatePartnerContact,
  deletePartnerContact,
  PartnerResponse,
  PartnerUpdateRequest,
  PartnerContactResponse,
  PartnerContactCreateRequest,
  PartnerContactUpdateRequest,
} from '../api/partner';

const AVATAR_COLORS = ['#0B4EC4', '#1B9E5A', '#8B5CF6', '#C6771A', '#E5484D', '#0EA5E9'];
const inputCls =
  'h-[42px] w-full rounded-xl border border-gray-300 bg-white px-3.5 text-[14px] font-medium text-gray-900 placeholder:text-gray-400 focus:border-primary-400 focus:outline-none focus:ring-2 focus:ring-primary-100';
const labelCls = 'mb-1.5 block text-[12.5px] font-bold text-gray-500';

/**
 * 거래처 상세 페이지
 *
 * 기능: 조회 / 수정·삭제(ADMIN) / 직원 목록·추가·수정·삭제(ADMIN)
 * 데스크톱: 좌측 정보 + 우측 고정 요약/액션 레일 / 모바일: 전용 읽기 뷰
 */
const PartnerDetailPage: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { user } = useAuthStore();
  const isAdmin = user?.role === 'ADMIN';
  const isMobile = useIsMobile();
  const goBack = useSmartBack('/partners');

  // 거래처 데이터
  const [partner, setPartner] = useState<PartnerResponse | null>(null);
  const [contacts, setContacts] = useState<PartnerContactResponse[]>([]);
  const [loading, setLoading] = useState<boolean>(false);
  const [error, setError] = useState<string | null>(null);

  // 편집 모드
  const [isEditing, setIsEditing] = useState<boolean>(false);

  // 편집 모드 상태
  const [companyName, setCompanyName] = useState<string>('');
  const [mainPhone, setMainPhone] = useState<string>('');
  const [address, setAddress] = useState<string>('');
  const [description, setDescription] = useState<string>('');

  // 거래처 직원 추가/편집
  const [showContactForm, setShowContactForm] = useState<boolean>(false);
  const [editingContactId, setEditingContactId] = useState<number | null>(null);
  const [newContact, setNewContact] = useState<Partial<PartnerContactResponse>>({
    name: '',
    position: '',
    department: '',
    phone: '',
    email: '',
  });
  const [loadingContact, setLoadingContact] = useState<boolean>(false);

  // 거래처 데이터 로딩
  useEffect(() => {
    if (!id) return;

    const loadPartner = async () => {
      setLoading(true);
      setError(null);
      try {
        const partnerId = parseInt(id, 10);
        const [partnerData, contactsData] = await Promise.all([
          getPartnerDetail(partnerId),
          getPartnerContacts(partnerId),
        ]);
        setPartner(partnerData);
        setContacts(contactsData);
      } catch (error: any) {
        console.error('거래처 로딩 실패:', error);
        if (error.response?.status === 404) {
          setError('거래처를 찾을 수 없습니다.');
        } else {
          setError('거래처 정보를 불러오는데 실패했습니다.');
        }
      } finally {
        setLoading(false);
      }
    };

    loadPartner();
  }, [id]);

  // 거래처 데이터를 편집 모드 상태로 복사
  useEffect(() => {
    if (partner && isEditing) {
      setCompanyName(partner.companyName || '');
      setMainPhone(partner.mainPhone || '');
      setAddress(partner.address || '');
      setDescription(partner.description || '');
    }
  }, [partner, isEditing]);

  // 편집 모드 진입
  const handleEdit = () => {
    setIsEditing(true);
  };

  // 편집 취소
  const handleCancel = () => {
    setIsEditing(false);
    setError(null);
    if (partner) {
      setCompanyName(partner.companyName || '');
      setMainPhone(partner.mainPhone || '');
      setAddress(partner.address || '');
      setDescription(partner.description || '');
    }
  };

  // 거래처 수정 저장
  const handleSave = async () => {
    if (!id || !partner) return;
    setError(null);

    if (!companyName.trim()) {
      setError('회사명을 입력해주세요.');
      return;
    }

    setLoading(true);

    try {
      const partnerId = parseInt(id, 10);
      const updateRequest: PartnerUpdateRequest = {
        id: partnerId,
        companyName: companyName.trim(),
        mainPhone: mainPhone.trim() || null,
        address: address.trim() || null,
        description: description.trim() || null,
      };

      const updatedPartner = await updatePartner(updateRequest);
      setPartner(updatedPartner);
      setIsEditing(false);
      setError(null);
    } catch (error: any) {
      console.error('거래처 수정 실패:', error);
      setError(error.response?.data?.message || '거래처 수정에 실패했습니다.');
    } finally {
      setLoading(false);
    }
  };

  // 거래처 삭제
  const handleDelete = async () => {
    if (!id) return;

    const confirmed = window.confirm('정말로 이 거래처를 삭제하시겠습니까? 이 작업은 되돌릴 수 없습니다.');
    if (!confirmed) return;

    setLoading(true);
    setError(null);

    try {
      const partnerId = parseInt(id, 10);
      await deletePartner(partnerId);
      navigate('/partners');
    } catch (error: any) {
      console.error('거래처 삭제 실패:', error);
      setError(error.response?.data?.message || '거래처 삭제에 실패했습니다.');
    } finally {
      setLoading(false);
    }
  };

  // 직원 추가 폼 열기
  const handleOpenContactForm = () => {
    setShowContactForm(true);
    setEditingContactId(null);
    setNewContact({ name: '', position: '', department: '', phone: '', email: '' });
    setError(null);
  };

  // 직원 편집 폼 열기
  const handleEditContact = (contact: PartnerContactResponse) => {
    setShowContactForm(true);
    setEditingContactId(contact.id);
    setNewContact({
      name: contact.name,
      position: contact.position || '',
      department: contact.department || '',
      phone: contact.phone || '',
      email: contact.email || '',
    });
    setError(null);
  };

  // 직원 폼 취소
  const handleCancelContactForm = () => {
    setShowContactForm(false);
    setEditingContactId(null);
    setNewContact({ name: '', position: '', department: '', phone: '', email: '' });
    setError(null);
  };

  // 직원 추가/수정
  const handleSaveContact = async () => {
    if (!id) return;
    setError(null);

    if (!newContact.name?.trim()) {
      setError('이름을 입력해주세요.');
      return;
    }

    setLoadingContact(true);

    try {
      const partnerId = parseInt(id, 10);

      if (editingContactId) {
        const updateRequest: PartnerContactUpdateRequest = {
          id: editingContactId,
          name: newContact.name.trim(),
          position: newContact.position?.trim() || null,
          department: newContact.department?.trim() || null,
          phone: newContact.phone?.trim() || null,
          email: newContact.email?.trim() || null,
        };

        const updatedContact = await updatePartnerContact(partnerId, updateRequest);
        setContacts(contacts.map((c) => (c.id === editingContactId ? updatedContact : c)));
      } else {
        const createRequest: PartnerContactCreateRequest = {
          name: newContact.name.trim(),
          position: newContact.position?.trim() || null,
          department: newContact.department?.trim() || null,
          phone: newContact.phone?.trim() || null,
          email: newContact.email?.trim() || null,
        };

        const createdContact = await createPartnerContact(partnerId, createRequest);
        setContacts([...contacts, createdContact]);
      }

      handleCancelContactForm();
    } catch (error: any) {
      console.error('거래처 직원 추가/수정 실패:', error);
      setError(error.response?.data?.message || '직원 추가/수정에 실패했습니다.');
    } finally {
      setLoadingContact(false);
    }
  };

  // 직원 삭제
  const handleDeleteContact = async (contactId: number) => {
    if (!id) return;

    const confirmed = window.confirm('정말로 이 직원을 삭제하시겠습니까?');
    if (!confirmed) return;

    setLoadingContact(true);
    setError(null);

    try {
      const partnerId = parseInt(id, 10);
      await deletePartnerContact(partnerId, contactId);
      setContacts(contacts.filter((c) => c.id !== contactId));
    } catch (error: any) {
      console.error('거래처 직원 삭제 실패:', error);
      setError(error.response?.data?.message || '직원 삭제에 실패했습니다.');
    } finally {
      setLoadingContact(false);
    }
  };

  useScrollLock(showContactForm);

  if (loading && !partner) {
    return (
      <div className="p-6">
        <div className="py-8 text-center text-gray-500">로딩 중...</div>
      </div>
    );
  }

  if (!partner) {
    return (
      <div className="p-6">
        <Alert type="error" message={error || '거래처를 찾을 수 없습니다.'} />
        <button
          onClick={() => navigate('/partners')}
          className="mt-4 rounded-lg bg-primary-500 px-4 py-2 text-white transition-colors hover:bg-primary-600"
        >
          목록으로
        </button>
      </div>
    );
  }

  // ── 모바일 읽기 뷰 (편집은 아래 공용 폼 재사용) ──
  if (isMobile && !isEditing) {
    return (
      <div className="min-h-full bg-gray-50 pb-10">
        <div className="flex items-center gap-1 px-2.5 pb-3 pt-3">
          <button onClick={goBack} aria-label="뒤로" className="flex h-10 w-10 items-center justify-center rounded-xl text-gray-600 active:bg-gray-100">
            <svg className="h-5 w-5" fill="none" stroke="currentColor" strokeWidth={2.2} strokeLinecap="round" strokeLinejoin="round" viewBox="0 0 24 24"><path d="m15 18-6-6 6-6" /></svg>
          </button>
          <h1 className="flex-1 text-center text-[17px] font-extrabold tracking-tight text-gray-900">거래처 상세</h1>
          <span className="w-10" />
        </div>

        <div className="px-[18px]">
          {error && <Alert type="error" message={error} dismissible onClose={() => setError(null)} style={{ marginBottom: '1rem' }} />}

          <h2 className="text-[23px] font-extrabold leading-tight tracking-tight text-gray-900">{partner.companyName}</h2>

          {/* 전화·문자 바로가기 */}
          {partner.mainPhone && (
            <div className="mt-3.5 flex gap-2.5">
              <a href={`tel:${partner.mainPhone}`} className="flex flex-1 items-center justify-center gap-2 rounded-2xl bg-green-50 py-3 text-[15px] font-extrabold text-green-700">
                <svg className="h-5 w-5" fill="none" stroke="currentColor" strokeWidth={2} strokeLinecap="round" strokeLinejoin="round" viewBox="0 0 24 24"><path d="M22 16.92v3a2 2 0 0 1-2.18 2 19.79 19.79 0 0 1-8.63-3.07 19.5 19.5 0 0 1-6-6 19.79 19.79 0 0 1-3.07-8.67A2 2 0 0 1 4.11 2h3a2 2 0 0 1 2 1.72c.13.96.36 1.9.7 2.81a2 2 0 0 1-.45 2.11L8.09 9.91a16 16 0 0 0 6 6l1.27-1.27a2 2 0 0 1 2.11-.45c.9.34 1.85.57 2.81.7A2 2 0 0 1 22 16.92Z" /></svg>
                전화
              </a>
              <a href={`sms:${partner.mainPhone}`} className="flex flex-1 items-center justify-center gap-2 rounded-2xl bg-primary-50 py-3 text-[15px] font-extrabold text-primary-700">
                <svg className="h-5 w-5" fill="none" stroke="currentColor" strokeWidth={2} strokeLinecap="round" strokeLinejoin="round" viewBox="0 0 24 24"><path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2Z" /></svg>
                문자
              </a>
            </div>
          )}

          {/* 정보 */}
          <div className="mt-4 rounded-2xl border border-gray-200 bg-white px-4 shadow-sm">
            <PRow label="대표전화" value={partner.mainPhone || '-'} />
            <PRow label="주소" value={partner.address || '-'} />
            <PRow label="설명" value={partner.description || '-'} last />
          </div>

          {/* 담당자 */}
          <div className="mb-2 mt-5 flex items-baseline gap-2 px-0.5">
            <span className="text-[15px] font-extrabold text-gray-900">담당자</span>
            <span className="text-[13px] font-bold text-gray-400">{contacts.length}</span>
          </div>
          {contacts.length === 0 ? (
            <div className="rounded-2xl border border-dashed border-gray-300 py-8 text-center text-[13.5px] font-semibold text-gray-400">등록된 담당자가 없어요</div>
          ) : (
            <div className="overflow-hidden rounded-2xl border border-gray-200 bg-white shadow-sm">
              {contacts.map((c, i) => (
                <div key={c.id} className={`flex items-center gap-3 px-4 py-3 ${i > 0 ? 'border-t border-gray-100' : ''}`}>
                  <div className="min-w-0 flex-1">
                    <div className="truncate text-[15px] font-bold text-gray-900">
                      {c.name}
                      {(c.position || c.department) && <span className="text-[12.5px] font-semibold text-gray-400"> · {[c.department, c.position].filter(Boolean).join(' ')}</span>}
                    </div>
                    {c.phone && <div className="text-[12.5px] font-medium text-gray-500">{c.phone}</div>}
                  </div>
                  {c.phone && (
                    <a href={`tel:${c.phone}`} className="flex h-10 w-10 flex-none items-center justify-center rounded-full bg-green-50 text-green-600" aria-label={`${c.name} 전화`}>
                      <svg className="h-5 w-5" fill="none" stroke="currentColor" strokeWidth={2} strokeLinecap="round" strokeLinejoin="round" viewBox="0 0 24 24"><path d="M22 16.92v3a2 2 0 0 1-2.18 2 19.79 19.79 0 0 1-8.63-3.07 19.5 19.5 0 0 1-6-6 19.79 19.79 0 0 1-3.07-8.67A2 2 0 0 1 4.11 2h3a2 2 0 0 1 2 1.72c.13.96.36 1.9.7 2.81a2 2 0 0 1-.45 2.11L8.09 9.91a16 16 0 0 0 6 6l1.27-1.27a2 2 0 0 1 2.11-.45c.9.34 1.85.57 2.81.7A2 2 0 0 1 22 16.92Z" /></svg>
                    </a>
                  )}
                </div>
              ))}
            </div>
          )}

          {isAdmin && (
            <div className="mt-6 flex gap-2.5">
              <button onClick={handleEdit} className="flex-1 rounded-2xl bg-primary-500 py-4 text-[16px] font-extrabold text-white shadow-sm active:scale-[0.99]">수정</button>
              <button onClick={handleDelete} className="flex-1 rounded-2xl border border-red-200 bg-white py-4 text-[16px] font-extrabold text-red-500 active:scale-[0.99]">삭제</button>
            </div>
          )}
        </div>
      </div>
    );
  }

  // ── 데스크톱 뷰 (+ 모바일 편집) ──
  const previewName = isEditing ? companyName : partner.companyName;
  const previewPhone = isEditing ? mainPhone : partner.mainPhone;
  const previewAddress = isEditing ? address : partner.address;

  return (
    <div className="mx-auto max-w-[1080px] px-5 py-6 sm:px-6">
      <button
        onClick={() => navigate('/partners')}
        className="mb-4 inline-flex items-center gap-1.5 text-[13px] font-bold text-gray-500 transition-colors hover:text-gray-800"
      >
        <svg className="h-4 w-4" fill="none" stroke="currentColor" strokeWidth={2.2} strokeLinecap="round" strokeLinejoin="round" viewBox="0 0 24 24">
          <path d="M15 18l-6-6 6-6" />
        </svg>
        거래처 목록으로
      </button>
      <div className="mb-5 flex items-center gap-2.5">
        <h1 className="text-[22px] font-extrabold tracking-tight text-gray-900">{partner.companyName}</h1>
        {isEditing && <span className="rounded-full bg-amber-50 px-2.5 py-0.5 text-[11.5px] font-bold text-amber-700">편집 중</span>}
      </div>

      {error && <Alert type="error" message={error} dismissible onClose={() => setError(null)} style={{ marginBottom: '1.25rem' }} />}

      <div className="lg:grid lg:grid-cols-[minmax(0,1fr)_300px] lg:items-start lg:gap-5">
        {/* 좌: 정보 */}
        <div className="min-w-0 space-y-4 lg:space-y-5">
          {/* 기본 정보 */}
          <section className="rounded-2xl border border-gray-200 bg-white p-5 shadow-sm sm:p-6">
            <h2 className="mb-5 text-[15.5px] font-extrabold tracking-tight text-gray-900">기본 정보</h2>
            {isEditing ? (
              <div className="space-y-4">
                <div>
                  <label className={labelCls}>
                    회사명 <span className="text-red-500">*</span>
                  </label>
                  <input type="text" value={companyName} onChange={(e) => setCompanyName(e.target.value)} className={inputCls} placeholder="회사명을 입력하세요" />
                </div>
                <div className="grid gap-4 sm:grid-cols-2">
                  <div>
                    <label className={labelCls}>대표 전화번호</label>
                    <input type="tel" value={mainPhone} onChange={(e) => setMainPhone(e.target.value)} className={inputCls} placeholder="02-000-0000" />
                  </div>
                  <div>
                    <label className={labelCls}>주소</label>
                    <input type="text" value={address} onChange={(e) => setAddress(e.target.value)} className={inputCls} placeholder="주소를 입력하세요" />
                  </div>
                </div>
                <div>
                  <label className={labelCls}>설명</label>
                  <textarea value={description} onChange={(e) => setDescription(e.target.value)} rows={3} className={`${inputCls} h-auto py-2.5 leading-relaxed`} placeholder="거래처에 대한 설명을 입력하세요" />
                </div>
              </div>
            ) : (
              <dl className="divide-y divide-gray-100">
                <DRow label="대표 전화" value={partner.mainPhone || '-'} mono />
                <DRow label="주소" value={partner.address || '-'} />
                <DRow label="설명" value={partner.description || '-'} preLine />
              </dl>
            )}
          </section>

          {/* 거래처 직원 */}
          <section className="rounded-2xl border border-gray-200 bg-white p-5 shadow-sm sm:p-6">
            <div className="mb-4 flex items-center gap-2.5">
              <h2 className="text-[15.5px] font-extrabold tracking-tight text-gray-900">거래처 직원</h2>
              {contacts.length > 0 && (
                <span className="inline-grid h-5 min-w-[20px] place-items-center rounded-full bg-primary-50 px-1.5 text-[11.5px] font-extrabold text-primary-600">{contacts.length}</span>
              )}
              <div className="flex-1" />
              {isAdmin && isEditing && (
                <button
                  type="button"
                  onClick={handleOpenContactForm}
                  className="inline-flex items-center gap-1.5 rounded-lg bg-primary-500 px-3.5 py-2 text-[13px] font-bold text-white shadow-sm shadow-primary-500/25 transition-colors hover:bg-primary-600"
                >
                  <svg className="h-4 w-4" fill="none" stroke="currentColor" strokeWidth={2.6} strokeLinecap="round" viewBox="0 0 24 24"><path d="M12 5v14M5 12h14" /></svg>
                  직원 추가
                </button>
              )}
            </div>

            {contacts.length === 0 ? (
              <div className="rounded-2xl border border-dashed border-gray-300 py-8 text-center text-[13px] font-semibold text-gray-400">
                등록된 직원이 없어요.
              </div>
            ) : (
              <div className="space-y-2.5">
                {contacts.map((contact, i) => (
                  <div key={contact.id} className="flex items-center gap-3 rounded-xl border border-gray-200 px-3.5 py-3">
                    <span
                      className="flex h-9 w-9 flex-none items-center justify-center rounded-full text-[13px] font-bold text-white"
                      style={{ backgroundColor: AVATAR_COLORS[i % AVATAR_COLORS.length] }}
                    >
                      {contact.name.charAt(0)}
                    </span>
                    <div className="min-w-0 flex-1">
                      <div className="flex items-center gap-2">
                        <span className="truncate text-[14px] font-extrabold text-gray-900">{contact.name}</span>
                        {contact.position && <span className="flex-none rounded-full bg-gray-100 px-2 py-0.5 text-[11px] font-bold text-gray-600">{contact.position}</span>}
                      </div>
                      <div className="mt-1 flex flex-wrap gap-x-3 gap-y-0.5 text-[12.5px] font-semibold text-gray-500">
                        {contact.department && <span>{contact.department}</span>}
                        {contact.phone && <span className="tabular-nums">{contact.phone}</span>}
                        {contact.email && <span className="truncate">{contact.email}</span>}
                      </div>
                    </div>
                    {isAdmin && isEditing && (
                      <div className="flex flex-none items-center gap-1">
                        <button
                          type="button"
                          onClick={() => handleEditContact(contact)}
                          aria-label="직원 수정"
                          className="flex h-8 w-8 items-center justify-center rounded-lg text-gray-400 hover:bg-gray-100 hover:text-primary-600"
                        >
                          <svg className="h-[17px] w-[17px]" fill="none" stroke="currentColor" strokeWidth={2} strokeLinecap="round" strokeLinejoin="round" viewBox="0 0 24 24"><path d="M12 20h9M16.5 3.5a2.1 2.1 0 0 1 3 3L7 19l-4 1 1-4Z" /></svg>
                        </button>
                        <button
                          type="button"
                          onClick={() => handleDeleteContact(contact.id)}
                          aria-label="직원 삭제"
                          className="flex h-8 w-8 items-center justify-center rounded-lg text-gray-400 hover:bg-red-50 hover:text-red-500"
                        >
                          <svg className="h-[17px] w-[17px]" fill="none" stroke="currentColor" strokeWidth={2} strokeLinecap="round" strokeLinejoin="round" viewBox="0 0 24 24"><path d="M3 6h18M8 6V4h8v2M19 6l-1 14H6L5 6" /></svg>
                        </button>
                      </div>
                    )}
                  </div>
                ))}
              </div>
            )}
          </section>
        </div>

        {/* 우: 요약 + 액션 레일 */}
        <div className="mt-4 space-y-3.5 lg:sticky lg:top-4 lg:mt-0">
          <div className="overflow-hidden rounded-2xl border border-gray-200 bg-white shadow-sm">
            <div className="px-[18px] pt-3.5 text-[11.5px] font-extrabold tracking-wide text-gray-400">거래처</div>
            <div className="flex items-center gap-2.5 border-b border-gray-200 px-[18px] pb-3.5 pt-1.5">
              <span className="flex h-8 w-8 flex-none items-center justify-center rounded-lg bg-primary-50 text-[13px] font-extrabold text-primary-600">
                {previewName?.charAt(0) || '거'}
              </span>
              <span className="truncate text-[15px] font-extrabold tracking-tight text-gray-900">{previewName || '거래처명'}</span>
            </div>
            <div className="flex items-center justify-between gap-3 border-b border-gray-200 px-[18px] py-2.5">
              <span className="text-[12.5px] font-bold text-gray-400">대표 전화</span>
              <span className="truncate text-[13px] font-bold text-gray-800 tabular-nums">{previewPhone || '—'}</span>
            </div>
            <div className="flex items-center justify-between gap-3 border-b border-gray-200 px-[18px] py-2.5">
              <span className="flex-none text-[12.5px] font-bold text-gray-400">주소</span>
              <span className="truncate text-[13px] font-bold text-gray-800">{previewAddress || '—'}</span>
            </div>
            <div className="flex items-center justify-between gap-3 px-[18px] py-2.5">
              <span className="text-[12.5px] font-bold text-gray-400">직원</span>
              <span className="text-[13px] font-bold text-gray-800 tabular-nums">{contacts.length}명</span>
            </div>
          </div>

          {isAdmin && (
            <div className="space-y-2.5">
              {isEditing ? (
                <>
                  <button
                    type="button"
                    onClick={handleSave}
                    disabled={loading}
                    className="flex h-11 w-full items-center justify-center rounded-xl bg-primary-500 text-[14.5px] font-extrabold text-white shadow-md shadow-primary-500/30 transition-colors hover:bg-primary-600 disabled:cursor-not-allowed disabled:bg-gray-400"
                  >
                    {loading ? '저장 중…' : '저장'}
                  </button>
                  <button
                    type="button"
                    onClick={handleCancel}
                    className="flex h-11 w-full items-center justify-center rounded-xl border border-gray-300 bg-white text-[14px] font-bold text-gray-600 transition-colors hover:bg-gray-50"
                  >
                    취소
                  </button>
                </>
              ) : (
                <>
                  <button
                    type="button"
                    onClick={handleEdit}
                    className="flex h-11 w-full items-center justify-center rounded-xl bg-primary-500 text-[14.5px] font-extrabold text-white shadow-md shadow-primary-500/30 transition-colors hover:bg-primary-600"
                  >
                    수정
                  </button>
                  <button
                    type="button"
                    onClick={handleDelete}
                    className="flex h-11 w-full items-center justify-center rounded-xl border border-red-200 bg-white text-[14px] font-bold text-red-500 transition-colors hover:bg-red-50"
                  >
                    삭제
                  </button>
                </>
              )}
            </div>
          )}
        </div>
      </div>

      {/* 직원 추가/수정 — 모달(데스크톱 중앙) / 하단 시트(모바일) */}
      {showContactForm && (
        <div className="fixed inset-0 z-50 flex items-end justify-center sm:items-center sm:p-6">
          <div className="absolute inset-0 bg-gray-900/50" onClick={handleCancelContactForm} />
          <div className="relative flex max-h-[88vh] w-full flex-col overflow-hidden rounded-t-3xl bg-white shadow-2xl sm:max-w-[540px] sm:rounded-2xl">
            <div className="flex items-center justify-between border-b border-gray-200 px-5 py-4">
              <h3 className="text-[17px] font-extrabold text-gray-900">{editingContactId ? '직원 수정' : '직원 추가'}</h3>
              <button
                type="button"
                onClick={handleCancelContactForm}
                aria-label="닫기"
                className="flex h-8 w-8 items-center justify-center rounded-lg text-gray-400 hover:bg-gray-100 hover:text-gray-600"
              >
                <svg className="h-5 w-5" fill="none" stroke="currentColor" strokeWidth={2} strokeLinecap="round" viewBox="0 0 24 24"><path d="M6 6l12 12M6 18 18 6" /></svg>
              </button>
            </div>

            <div className="flex flex-col gap-4 overflow-y-auto overscroll-contain px-5 py-5">
              <div>
                <label className={labelCls}>
                  이름 <span className="text-red-500">*</span>
                </label>
                <input type="text" value={newContact.name || ''} onChange={(e) => setNewContact({ ...newContact, name: e.target.value })} className={inputCls} placeholder="이름을 입력하세요" />
              </div>
              <div className="grid gap-4 sm:grid-cols-2">
                <div>
                  <label className={labelCls}>직급</label>
                  <input type="text" value={newContact.position || ''} onChange={(e) => setNewContact({ ...newContact, position: e.target.value })} className={inputCls} placeholder="예: 구매 부장" />
                </div>
                <div>
                  <label className={labelCls}>부서</label>
                  <input type="text" value={newContact.department || ''} onChange={(e) => setNewContact({ ...newContact, department: e.target.value })} className={inputCls} placeholder="예: 구매팀" />
                </div>
              </div>
              <div className="grid gap-4 sm:grid-cols-2">
                <div>
                  <label className={labelCls}>전화번호</label>
                  <input type="tel" value={newContact.phone || ''} onChange={(e) => setNewContact({ ...newContact, phone: e.target.value })} className={inputCls} placeholder="010-0000-0000" />
                </div>
                <div>
                  <label className={labelCls}>이메일</label>
                  <input type="email" value={newContact.email || ''} onChange={(e) => setNewContact({ ...newContact, email: e.target.value })} className={inputCls} placeholder="name@company.co.kr" />
                </div>
              </div>
              {error && <p className="text-[13px] font-semibold text-red-500">{error}</p>}
            </div>

            <div className="flex justify-end gap-2.5 border-t border-gray-200 bg-gray-50 px-5 py-4">
              <button
                type="button"
                onClick={handleCancelContactForm}
                className="flex h-[42px] items-center rounded-xl border border-gray-300 bg-white px-4 text-[14px] font-bold text-gray-600 hover:bg-gray-100"
              >
                취소
              </button>
              <button
                type="button"
                onClick={handleSaveContact}
                disabled={loadingContact}
                className="flex h-[42px] items-center rounded-xl bg-primary-500 px-5 text-[14px] font-extrabold text-white shadow-sm shadow-primary-500/30 hover:bg-primary-600 disabled:cursor-not-allowed disabled:bg-gray-400"
              >
                {loadingContact ? '저장 중…' : editingContactId ? '수정' : '추가'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

/** 모바일 상세 정보 행. */
const PRow: React.FC<{ label: string; value: string; last?: boolean }> = ({ label, value, last }) => (
  <div className={`flex gap-3 py-3.5 ${last ? '' : 'border-b border-gray-100'}`}>
    <span className="w-16 flex-none text-[13px] font-semibold text-gray-400">{label}</span>
    <span className="min-w-0 flex-1 whitespace-pre-wrap text-[15px] font-bold text-gray-900">{value}</span>
  </div>
);

/** 데스크톱 읽기 모드 정보 행. */
const DRow: React.FC<{ label: string; value: string; mono?: boolean; preLine?: boolean }> = ({ label, value, mono, preLine }) => (
  <div className="flex gap-4 py-3">
    <span className="w-20 flex-none text-[13px] font-bold text-gray-400">{label}</span>
    <span className={`min-w-0 flex-1 text-[14px] font-semibold text-gray-800 ${mono ? 'tabular-nums' : ''} ${preLine ? 'whitespace-pre-wrap' : ''}`}>{value}</span>
  </div>
);

export default PartnerDetailPage;
